import React, { Fragment, useEffect, useMemo, useState } from 'react'
import useFormV2 from '../../form-v2/form.hook';
import { getRecaptchaToken } from '../../google';
import FormV2 from '../../form-v2/form.component';
import useFetch from '../../hooks/fetch.hook';
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import forgotPasswordImage from '../../assets/images/2fa_forgot_password.png';
import { API_ENDPOINT, getAgeLimit, translateAgeLimitMessage } from '../../constants';
import useFetchData from '../../hooks/fetch-data.hook';
import * as Utils from '../../form-v2/utils';
import GenderV2Field from '../../form-v2/custom-fields/genderV2.field';
import { Player } from '@lottiefiles/react-lottie-player';
import bankidIcon from '../../assets/images/bankid.png';
import mitIdIcon from '../../assets/images/mitid.png';

async function onBeforeSubmit(data, _, externalState) {
    const _token = await getRecaptchaToken(data._action);
    data._token = _token;
    data._codeData = externalState?.codeData;
    data._clinicGroupId = externalState?.props?.groupObj?._id;
    if (!data._clinicGroupId && externalState?.groupData?._id) data._clinicGroupId = externalState?.groupData?._id;
    if (externalState?.resetPasswordToken) data._resetPasswordToken = externalState?.resetPasswordToken;
    if (externalState?.accountType) data._accountType = externalState?.accountType;
    if (externalState?.lang) data._lang = externalState?.lang;
    if (externalState?.emailCodeData) data._emailCodeData = externalState?.emailCodeData;
    if (externalState?.phoneCodeData) data._phoneCodeData = externalState?.phoneCodeData;
    if (externalState?.verification) data._verification = externalState?.verification;
    return data;
}

function getHideLangSelector() {
    if (typeof window !== 'undefined' && localStorage) {
        const secondaryLanguageDisabled = localStorage?.getItem?.('secondaryLanguageDisabled');
        if (secondaryLanguageDisabled && secondaryLanguageDisabled === 'yes') return true;
    }

    return false;
}

const passwordHas8Chars = (string) => {
    return string?.trim?.()?.length >= 8 ? true : false;
}

const passwordHasUpperCase = (string) => {
    return /[A-Z]/.test(string);
}

const passwordHasLowerCase = (string) => {
    return /[a-z]/.test(string);
}

const passwordHasNumber = (string) => {
    return /[0-9]/.test(string);
}

function verificationIsNeeded({
    groupData,
    email,
    existingEmail,
    emailIsVerified,
    phone,
    existingPhone,
    phoneIsVerified,
}) {
    let obj = {
        email: true,
        phone: true,
    }

    if (email && existingEmail && email.trim().toLocaleLowerCase() === existingEmail.trim().toLocaleLowerCase() && emailIsVerified) obj.email = false;

    if (groupData?.useOnlyEmailFor2fa) obj.phone = false;
    if (phone && existingPhone && phone.trim() === existingPhone.trim() && phoneIsVerified) obj.phone = false;

    return obj;
}

function _getCodeData(type, clinicGroupId) {
    try {
        const name = `codeData_${type}`;
        if (typeof window !== 'undefined' && localStorage) {
            const codeDataStr = localStorage?.getItem?.(name);
            if (codeDataStr) {
                const parsed = JSON.parse(codeDataStr);
                if (parsed && typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                    if (
                        parsed.codeId &&
                        parsed.userId &&
                        parsed.expiresAt &&
                        (+parsed.expiresAt) > Date.now() &&
                        parsed.clinicGroupId &&
                        parsed.clinicGroupId === clinicGroupId
                    ) {
                        return parsed;
                    }
                }
            }
        }

        _removeCodeData(type);
    } catch (err) { }

    return null;
}

function _setCodeData(type, data) {
    try {
        const name = `codeData_${type}`;
        if (typeof window !== 'undefined' && localStorage) {
            const codeDataStr = JSON.stringify(data);
            localStorage?.setItem?.(name, codeDataStr);
        }
    } catch (err) { }
}

function _removeCodeData(type) {
    try {
        const name = `codeData_${type}`;
        if (typeof window !== 'undefined' && localStorage) {
            localStorage?.removeItem?.(name);
        }
    } catch (err) { }
}

const TwoFactorAuthV2LoginComponent = (props) => {
    const { data: groupData, } = useFetchData(props?.alias ? '/data/clinic-group/info' : null, {
        method: 'post',
        data: {
            alias: props?.alias,
        }
    });
    const [forgotPassword, setForgotPassword] = useState(false);
    const [register, setRegister] = useState(false);
    const [email, setEmail] = useState('');

    useEffect(() => {
        props?.checkSSNSwitchCountries?.();
    }, [])

    return <>
        <Login
            props={props}
            email={email}
            setEmail={setEmail}
            setRegister={setRegister}
            setForgotPassword={setForgotPassword}
            groupData={groupData}
        />
        {register ? (
            <Register
                props={props}
                setRegister={setRegister}
                register={register}
                groupData={groupData}
            />
        ) : null}
        {forgotPassword ? (
            <ForgotPassword
                props={props}
                email={email}
                setEmail={setEmail}
                forgotPassword={forgotPassword}
                setForgotPassword={setForgotPassword}
            />
        ) : null}
    </>
}

export default TwoFactorAuthV2LoginComponent;

const Login = ({
    props,
    email,
    setEmail,
    setRegister,
    setForgotPassword,
    groupData
}) => {
    const [tempMessage, setTempMessage] = useState(null);
    const [verification, setVerification] = useState(null);
    const [phoneCodeData, setPhoneCodeData] = useState(null);
    const [phoneTempMessage, setPhoneTempMessage] = useState(null);
    const [codeData, setCodeData] = useState(() => _getCodeData('login', props?.groupObj?._id));

    const _fetch = useFetch();

    useEffect(() => {
        let timer = null;
        props?.setHideOtherAuthOptions?.(codeData || verification ? true : false, codeData ? (codeData?.device ?? null) : verification ? (verification?.device ?? null) : null);
        if (codeData) {
            if (typeof document !== 'undefined') {
                timer = setTimeout(() => {
                    const el = document.getElementById('2fa_login_code');
                    if (el) el.focus();
                }, 1000);
            }
        }

        return () => {
            if (timer) clearTimeout(timer);
        }
    }, [codeData, verification]);

    useEffect(() => {
        let timer = null;
        if (tempMessage) {
            timer = setTimeout(() => {
                setTempMessage(null);
            }, 5000);
        }

        return () => {
            if (timer) clearTimeout(timer);
        }
    }, [tempMessage]);

    useEffect(() => {
        let timer = null;
        if (phoneTempMessage) timer = setTimeout(() => { setPhoneTempMessage(null) }, 5000);
        return () => { clearTimeout(timer) };
    }, [phoneTempMessage]);

    const form = useFormV2({
        autoComplete: true,
        autoSubmit: {
            onBeforeSubmit,
            method: 'post',
            url: '/2fa/login',
            onAfterSubmit: (data) => {
                if (data?.codeData) {
                    setCodeData(data.codeData);
                    _setCodeData('login', data.codeData);
                    setTempMessage(null);
                    setPhoneCodeData(null);
                    setPhoneTempMessage(null);
                    setVerification(null);
                    form?.set?.('_action', '2fa-login-code')
                }

                if (data?.token) {
                    // setCodeData(null);
                    _removeCodeData('login');
                    props.onLogin?.(data);
                }

                if (data?.verification) {
                    setVerification(data.verification);
                    if (data?.verification?.value) {
                        form?.set?.('phone', data.verification.value);
                    }
                    form?.set?.('_action', '2fa-verification');
                }
            },
            skipResetOnSuccess: true,
        },
        buttons: {
            submit: {
                custom: ({ lang }) => <button
                    type='submit'
                    className='auth-2fa-button'
                >
                    {'Log in'.translate(lang)}
                </button>
            }
        },
        classNames: {
            form: 'auth-2fa-form',
            submitError: 'auth-2fa-submit-error',
        },
        // showValidFields: true,
        useSubmitErrorForErrorMessage: true,
        onBeforeSubmitValidator: (data, _, e) => {
            if (e?.verification) {
                if (!data?.phoneCode || !Utils.isValidCode(data.phoneCode)) return 'Please fill in all required fields correctly!';
            }

            return null;
        }
    }, {
        _action: {
            type: 'hidden',
            initialValue: codeData ? '2fa-login-code' : '2fa-login'
        },
        email: {
            type: 'email',
            initialValue: email,
            isRequired: true,
            label: 'Email address',
            config: {
                autoComplete: 'email',
            },
            condition: (_, e) => e?.codeData || e?.verification ? false : true,
        },
        password: {
            type: 'password',
            isRequired: true,
            label: 'Password',
            config: {
                autoComplete: 'current-password',
                passwordV2: true,
                afterComponent: ({
                    data
                }) => {
                    return <button
                        type='button'
                        className='forgot-password-button'
                        onClick={() => {
                            data?.externalState?.onForgotPassword?.(data?.state?.email?.value)
                        }}
                    >
                        {'Forgot password?'.translate(data?.externalState?.props?.lang)}
                    </button>
                }
            },
            condition: (_, e) => e?.codeData || e?.verification ? false : true,
        },


        code: {
            type: 'code',
            id: '2fa_login_code',
            isRequired: true,
            // label: 'Code',
            condition: (_, e) => e?.codeData ? true : false,
            config: {
                beforeComponent: ({ data: { externalState } }) => externalState?.codeData?.value ? <div className='auth-2fa-code-before-component-container'>
                    <p>{'A 6-digit code has been sent to'.translate(externalState?.props?.lang)}</p>
                    <p>{externalState?.codeData?.device === 'phone' ? <strong>{'+ '}{externalState.codeData.value.slice(0, 2)}{' '}{externalState.codeData.value.slice(2)}</strong> : <strong>{externalState.codeData.value}</strong>}</p>
                </div> : null,
                afterComponent: ({ data: { externalState } }) => externalState?.codeData?.codeId ? <><ul className='auth-2fa-code-after-component-container'>
                    <li><p>{'Code is valid for'.translate(externalState?.props?.lang)}{' '}<strong>{'15 minutes'.translate(externalState?.props?.lang)}</strong></p></li>
                    <li><p>{"Didn't receive the code?".translate(externalState?.props?.lang)}{' '}<button
                        type='button'
                        onClick={async () => {
                            const data = await onBeforeSubmit({ _action: '2fa-login-code-resend' }, null, externalState);
                            const response = await _fetch('post', '/2fa/resend', data);
                            if (response.data?.message) externalState?.setTempMessage?.({
                                type: 'success',
                                message: response.data.message.translate(externalState?.props?.lang),
                            });
                            if (response.error) externalState?.setTempMessage?.({
                                type: 'error',
                                message: response.error.translate(externalState?.props?.lang),
                            });
                        }}
                    >
                        {'Resend'.translate(externalState?.props?.lang)}
                    </button></p></li>
                </ul>
                    {externalState?.tempMessage ? <div className={`auth-2fa-code-after-component-message ${externalState?.tempMessage?.type}`}>
                        {externalState?.tempMessage?.message}
                    </div> : null}
                </> : null,
            }
        },

        phone: {
            type: 'phone',
            isRequired: true,
            label: 'Phone number',
            // initialValue: '46198905129922',
            isDisabled: (_, e) => e?.phoneCodeData ? true : false,
            config: {
                // autoComplete: 'tel',
                min: 8,
                max: 20,
                afterComponent: ({ data: { externalState, submitAttempted, setSubmitError }, value, lang, isValid, hasError }) => {
                    if (externalState?.phoneCodeData && !externalState?.phoneTempMessage) return <></>;

                    const isDisabled = !value || !isValid || hasError;
                    return <div>
                        {externalState?.phoneTempMessage ? <div className={`auth-2fa-code-after-component-message ${externalState?.phoneTempMessage?.type}`}>
                            {externalState?.phoneTempMessage?.message}
                        </div> : null}
                        {externalState?.phoneCodeData ? null : <p className={`auth-2fa-register-text verification-text ${submitAttempted ? 'verification-text-error' : ''}`}>
                            {'*'}{'Your phone number must be verified. After typing in and double-checking your number, press the button to send code.'.translate(lang)}
                            <button
                                type='button'
                                className='register-button'
                                disabled={isDisabled}
                                onClick={async () => {
                                    if (!isDisabled) {
                                        const data = await onBeforeSubmit({
                                            _action: '2fa-phone-verification',
                                            _phoneForVerification: value,
                                            userId: externalState?.verification?.userId,
                                        }, {}, externalState);
                                        const response = await _fetch('post', '/2fa/create-account', data);
                                        if (response?.error) {
                                            externalState?.setPhoneTempMessage?.({ type: 'error', message: response.error.translate(lang) });
                                        }
                                        if (response?.data?.codeData) {
                                            setSubmitError(null);
                                            externalState?.setPhoneTempMessage?.({ type: 'success', message: 'An SMS with 6-digit code has been sent to your device!'.translate(lang) });
                                            externalState?.setPhoneCodeData?.(response.data.codeData);
                                        }
                                    }
                                }}
                            >
                                {'Send code'.translate(lang)}
                            </button>
                        </p>}
                    </div>;
                }
            },
            condition: (_, e) => e?.verification ? true : false,
        },
        phoneCode: {
            type: 'code',
            isRequired: true,
            label: 'Phone verification code',
            config: { autoComplete: 'one-time-code' },
            classes: (_, e) => e?.phoneCodeData ? { container: 'auth-2fa-verification-code-create-account' } : {},
            condition: (_, e) => e?.verification && e?.phoneCodeData ? true : false,
        }
    }, {
        props,
        groupData,
        onForgotPassword: (e) => {
            if (e) setEmail(e);
            setForgotPassword(true);
        },
        codeData,

        tempMessage,
        setTempMessage,

        verification,
        setVerification,
        phoneCodeData,
        setPhoneCodeData,
        phoneTempMessage,
        setPhoneTempMessage,
    });

    return <div>
        <FormV2
            data={form}
            lang={props.lang}
        />
        <p className='auth-2fa-register-text'>
            {codeData || verification ? <Fragment key='codeData'>
                {'Not your account?'.translate(props.lang)}
                {' '}
                <button
                    type='button'
                    className='register-button'
                    onClick={() => {
                        setCodeData(null);
                        _removeCodeData('login');
                        setVerification(null);
                        setPhoneCodeData(null);
                        setPhoneTempMessage(null);
                        setTempMessage(null);
                        form.set('_action', '2fa-login');
                    }}
                >
                    {'Cancel'.translate(props.lang)}
                </button>
            </Fragment> : <Fragment key='noCodeData'>
                {'Do not have an account yet?'.translate(props.lang)}
                {' '}
                <button
                    type='button'
                    className='register-button'
                    onClick={() => {
                        props?.checkSSNSwitchCountries?.();
                        setRegister(true);
                    }}
                >
                    {'Register now'.translate(props.lang)}
                </button>
            </Fragment>}
        </p>
    </div>
}

const Register = ({
    props,
    setRegister,
    register,
    groupData,
}) => {
    const [successMessage, setSuccessMessage] = useState(null);
    const [emailCodeData, setEmailCodeData] = useState(null);
    const [phoneCodeData, setPhoneCodeData] = useState(null);
    const [emailTempMessage, setEmailTempMessage] = useState(null);
    const [phoneTempMessage, setPhoneTempMessage] = useState(null);
    const [taken, setTaken] = useState({
        ssns: [],
        emails: [],
    });
    const [type, setType] = useState('adult'); // adult / child
    const [hasAttemptedSubmitAfterSendingCode, setHasAttemptedSubmitAfterSendingCode] = useState(false);
    const hideLangSelector = props?.secondaryLanguageDisabled ? true : false;
    const lang = props?.lang ?? 'en';

    const _fetch = useFetch();

    const { country, alias, clinicsAgeLimitExceptions, groupObj } = props;
    const groupId = (groupData?._id ?? groupObj?._id);
    const ageLimit = useMemo(() => getAgeLimit(country, (groupId ?? alias), clinicsAgeLimitExceptions), [country, groupId, alias, clinicsAgeLimitExceptions]);

    const buttonMessage = useMemo(() => {
        return translateAgeLimitMessage(
            type === 'adult' ? 'Create account for child (0-{AGE_LIMIT-1})' : 'Create account for adult ({AGE_LIMIT}+)',
            lang,
            ageLimit,
        )
    }, [type, ageLimit, lang]);

    const privacyPolicy = useMemo(() => {
        if (groupData?.privacyPolicy?.file) return `${API_ENDPOINT}${groupData?.privacyPolicy?.file}`;

        return null;
    }, [groupData]);

    const termsOfUse = useMemo(() => {
        if (groupData?.termsOfUse?.file) return `${API_ENDPOINT}${groupData?.termsOfUse?.file}`;

        return null;
    }, [groupData]);

    async function checkTaken(value) {
        if (Utils.isValidSSN(value) && value?.ssn) {
            const response = await _fetch('post', '/2fa/check/ssn', {
                ssn: value?.ssn,
            });
            if (response?.data?.ssns) {
                setTaken(prev => ({
                    emails: prev.emails,
                    ssns: [...prev.ssns, ...response.data.ssns],
                }));
                // setTimeout(() => { form?.triggerValidation?.(['child.ssn']); }, 100)
            }
        }
        if (Utils.isValidEmail(value) && value) {
            const response = await _fetch('post', '/2fa/check/email', {
                email: value,
            });
            if (response?.data?.emails) {
                setTaken(prev => ({
                    emails: [...prev.emails, ...response.data.emails],
                    ssns: prev.ssns,
                }));
                // setTimeout(() => { form?.triggerValidation?.(); }, 100)
            }
        }
    }

    useEffect(() => {
        let timer = null;
        if (emailTempMessage) timer = setTimeout(() => { setEmailTempMessage(null) }, 5000);
        return () => { clearTimeout(timer) };
    }, [emailTempMessage]);
    useEffect(() => {
        let timer = null;
        if (phoneTempMessage) timer = setTimeout(() => { setPhoneTempMessage(null) }, 5000);
        return () => { clearTimeout(timer) };
    }, [phoneTempMessage]);

    const today = useMemo(() => {
        const d = new Date();
        return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
    }, []);

    const form = useFormV2({
        autoComplete: true,
        autoSubmit: {
            onBeforeSubmit,
            method: 'post',
            url: '/2fa/create-account',
            onAfterSubmit: (data) => {
                if (data?.message) {
                    setSuccessMessage(data.message);
                    return;
                };
                if (data?.token) {
                    props.onLogin?.(data);
                }
                if (data?.emailCodeData) {
                    setEmailCodeData(data.emailCodeData);
                    if (data?.phoneCodeData) {
                        setPhoneCodeData(data.phoneCodeData);
                    }

                    form?.set?.('_action', '2fa-create-account');
                }
            },
            skipResetOnSuccess: true,
        },
        buttons: {
            submit: {
                custom: ({ data: { externalState } }) => {
                    const groupData = externalState?.groupData;
                    const lang = externalState?.props?.lang;
                    const emailCodeData = externalState?.emailCodeData;
                    const text = useMemo(() => {
                        if (!emailCodeData) return 'Send verification code(s)'.translate(lang);
                        if (groupData?.skip2faVerification) return 'Create account and Log in'.translate(lang);

                        return 'Create account'.translate(lang);
                    }, [groupData, lang, emailCodeData]);

                    return <>
                        <p className='auth-2fa-register-terms-of-use'>
                            {'By creating an account, you accept our'.translate(lang)}
                            {' '}
                            <strong>
                                {externalState?.termsOfUse ? <a
                                    href={externalState?.termsOfUse}
                                    target='_blank'
                                >
                                    {'Terms of Service'.translate(lang)}
                                </a> : <>
                                    {'Terms of Service'.translate(lang)}
                                </>}
                            </strong>
                        </p>
                        <button
                            type='submit'
                            className='auth-2fa-button'
                            onClick={() => {
                                if (externalState?.emailCodeData) {
                                    setHasAttemptedSubmitAfterSendingCode(true);
                                }
                            }}
                        >
                            {text}
                        </button>
                    </>
                }
            }
        },
        classNames: {
            form: 'auth-2fa-form',
            submitError: 'auth-2fa-submit-error',
            buttonsContainer: 'auth-2fa-buttons-container',
        },
        useSubmitErrorForErrorMessage: true,
        showValidFields: true,
        onBeforeSubmitValidator: (data, _, e) => {
            if (e?.emailCodeData) {
                if (!data?.emailCode || !Utils.isValidCode(data.emailCode)) return 'Please fill in all required fields correctly!';

                if (!e?.groupData?.useOnlyEmailFor2fa && (!data?.phoneCode || !Utils.isValidCode(data.phoneCode))) return 'Please fill in all required fields correctly!';
            }

            return null;
        },

    }, {
        _action: {
            type: 'hidden',
            initialValue: '2fa-verification'
        },
        actionType: {
            type: 'hidden',
            initialValue: 'verification',
            condition: (_, e) => e?.emailCodeData ? false : true
        },
        'child.ssn': {
            type: 'ssn',
            initialValue: {
                country: props?.country,
            },
            isRequired: true,
            condition: (_, e) => e?.accountType === 'child' && e?.props?.country && ['sweden', 'denmark'].includes(e?.props?.country) ? true : false,
            config: { autoComplete: 'off' },
            onChangeCallback: checkTaken,
            validator: (value, _, e) => {
                if (Utils.isValidSSN(value) && value?.ssn) {
                    if (e?.taken?.ssns && Array.isArray(e.taken.ssns) && e.taken.ssns.includes(value.ssn)) return 'Provided SSN belongs to another user!';

                    const _ageLimit = e?.ageLimit;

                    const age = Utils.getAge('ssn', value);
                    if (typeof age !== 'number') return 'Please enter a valid SSN!';
                    if (age >= _ageLimit) return 'A child must be under {AGE_LIMIT} years old!';
                } else {
                    console.log('else')
                    return 'Please enter a valid SSN!'
                }

                return null;
            }
        },
        'child.customId': {
            type: 'text',
            isRequired: (_, e) => e?.groupData?.patientInfoSettings?.isCustomIdEnabled && e?.groupData?.patientInfoSettings?.isCustomIdRequired ? true : false,
            label: (_, e) => e?.groupData?.patientInfoSettings?.customIdLabel ? e?.groupData?.patientInfoSettings?.customIdLabel : 'Custom id',
            description: (_, e) => e?.groupData?.patientInfoSettings?.customIdInfoText ? e?.groupData?.patientInfoSettings?.customIdInfoText : null,
            condition: (_, e) => e?.accountType === 'child' && e?.groupData?.patientInfoSettings?.isCustomIdEnabled ? true : false,
            instantTransformer: (value) => {
                value = value?.replace(/\s/g, '');
                return value
            },
            submitTransformer: (value) => {
                value = value?.replace(/\s/g, '');
                return value
            }
        },
        'child.name': {
            type: 'text',
            isRequired: true,
            label: 'First and last name',
            condition: (_, e) => e?.accountType === 'child' ? true : false,
        },
        'child.dob': {
            type: 'date',
            isRequired: true,
            label: 'Date of birth',
            condition: (_, e) => e?.accountType === 'child' && !['sweden', 'denmark'].includes(e?.props?.country) ? true : false,
            config: { dateType: 'dob', min: '1850-01-01', max: today },
            validator: (value, _, e) => {
                if (Utils.isValidDate(value) && value?.trim?.()) {
                    const _ageLimit = e?.ageLimit;
                    const age = Utils.getAge('dob', value);
                    if (typeof age !== 'number') return 'Please enter a valid date of birth!';
                    if (age >= _ageLimit) return 'A child must be under {AGE_LIMIT} years old!';
                } else {
                    return 'Please enter a valid date of birth!'
                };

                return null;
            }
        },
        'child.gender': {
            type: 'custom',
            // isRequired: true,
            initialValue: '',
            component: GenderV2Field,
            description: (_, e) => (e?.groupData?.patientInfoSettings?.canHaveOtherGender) ? undefined : "The patient's biological sex is needed for proper treatment",
            config: { autoComplete: 'sex', formFieldTag: 'fieldset' },
            condition: (_, e) => e?.accountType === 'child' ? true : false,
            classes: (_, e) => (e?.groupData?.patientInfoSettings?.canHaveOtherGender) ? ({ error: 'gender-radio-with-other-msg', description: 'gender-radio-with-other-msg' }) : ({}),
            validator: (value) => !value ? null : value === 'other' ? 'Please enter your preferred gender!' : null,
        },
        __parentGuardian: {
            type: 'custom',
            initialValue: '',
            component: ({ lang }) => <h6 style={{ marginTop: 10, fontSize: 20, fontWeight: '700' }}>{'Parent/Guardian'.translate(lang)}</h6>,
            condition: (_, e) => e?.accountType === 'child' ? true : false,
        },
        ssn: {
            type: 'ssn',
            initialValue: {
                country: props?.country,
            },
            isRequired: true,
            condition: (_, e) => e?.props?.country && ['sweden', 'denmark'].includes(e?.props?.country) ? true : false,
            config: { autoComplete: 'off' },
            onChangeCallback: checkTaken,
            validator: (value, _, e) => {
                if (Utils.isValidSSN(value) && value?.ssn) {
                    if (e?.taken?.ssns && Array.isArray(e.taken.ssns) && e.taken.ssns.includes(value.ssn)) return 'Provided SSN belongs to another user!';

                    const _ageLimit = e?.ageLimit;

                    const age = Utils.getAge('ssn', value);
                    if (typeof age !== 'number') return 'Please enter a valid SSN!';
                    if (age < _ageLimit) return `Only a parent/guardian can create an account for a child that is under {AGE_LIMIT} years old!`;
                } else {
                    console.log('else')
                    return 'Please enter a valid SSN!'
                }

                return null;
            }
        },
        customId: {
            type: 'text',
            isRequired: (_, e) => e?.groupData?.patientInfoSettings?.isCustomIdEnabled && e?.groupData?.patientInfoSettings?.isCustomIdRequired ? true : false,
            label: (_, e) => e?.groupData?.patientInfoSettings?.customIdLabel ? e?.groupData?.patientInfoSettings?.customIdLabel : 'Custom id',
            description: (_, e) => e?.groupData?.patientInfoSettings?.customIdInfoText ? e?.groupData?.patientInfoSettings?.customIdInfoText : null,
            condition: (_, e) => e?.groupData?.patientInfoSettings?.isCustomIdEnabled ? true : false,
            instantTransformer: (value) => {
                value = value?.replace(/\s/g, '');
                return value
            },
            submitTransformer: (value) => {
                value = value?.replace(/\s/g, '');
                return value
            }
        },
        name: {
            type: 'text',
            isRequired: true,
            label: 'First and last name',
            config: { autoComplete: 'name' }
        },
        dob: {
            type: 'date',
            isRequired: true,
            label: 'Date of birth',
            condition: (_, e) => !['sweden', 'denmark'].includes(e?.props?.country) ? true : false,
            config: { autoComplete: 'bday', dateType: 'dob', min: '1850-01-01', max: today },
            validator: (value, _, e) => {
                if (Utils.isValidDate(value) && value?.trim?.()) {
                    const _ageLimit = e?.ageLimit;
                    const age = Utils.getAge('dob', value);
                    if (typeof age !== 'number') return 'Please enter a valid date of birth!';
                    if (age < _ageLimit) return `Only a parent/guardian can create an account for a child that is under {AGE_LIMIT} years old!`;
                } else {
                    return 'Please enter a valid date of birth!'
                };

                return null;
            }
        },
        gender: {
            type: 'custom',
            // isRequired: true,
            initialValue: '',
            component: GenderV2Field,
            description: (_, e) => (e?.groupData?.patientInfoSettings?.canHaveOtherGender) ? undefined : "The patient's biological sex is needed for proper treatment",
            config: { autoComplete: 'sex', formFieldTag: 'fieldset' },
            classes: (_, e) => (e?.groupData?.patientInfoSettings?.canHaveOtherGender) ? ({ error: 'gender-radio-with-other-msg', description: 'gender-radio-with-other-msg' }) : ({}),
            validator: (value) => !value ? null : value === 'other' ? 'Please enter your preferred gender!' : null,
        },
        email: {
            type: 'email',
            isRequired: true,
            isDisabled: (_, e) => e?.emailCodeData ? true : false,
            label: 'Email address',
            config: {
                autoComplete: 'email',
                // afterComponent: ({ data: { externalState, submitAttempted, setSubmitError }, value, lang, isValid, hasError, isTouched }) => {
                //     const isDisabled = !value || !isValid || hasError || !isTouched;
                //     if (externalState?.emailCodeData && !externalState?.emailTempMessage) return <></>
                //     return <div>
                //         {externalState?.emailTempMessage ? <div className={`auth-2fa-code-after-component-message ${externalState?.emailTempMessage?.type}`}>
                //             {externalState?.emailTempMessage?.message}
                //         </div> : null}
                //         {externalState?.emailCodeData ? null : <p className={`auth-2fa-register-text verification-text ${submitAttempted ? 'verification-text-error' : ''}`}>
                //             {'*'}{'Your email address must be verified. After typing in and double-checking your email, press the button to send code.'.translate(lang)}
                //             <button
                //                 type='button'
                //                 className='register-button'
                //                 disabled={isDisabled}
                //                 onClick={async () => {
                //                     if (!isDisabled) {
                //                         const data = await onBeforeSubmit({
                //                             _action: '2fa-email-verification',
                //                             _emailForVerification: value,
                //                         }, {}, externalState);
                //                         const response = await _fetch('post', '/2fa/create-account', data);
                //                         if (response?.error) {
                //                             externalState?.setEmailTempMessage?.({ type: 'error', message: response.error.translate(lang) });
                //                         }
                //                         if (response?.data?.codeData) {
                //                             setSubmitError(null);
                //                             externalState?.setEmailTempMessage?.({ type: 'success', message: 'A 6-digit code has been sent to specified email address!'.translate(lang) });
                //                             externalState?.setEmailCodeData?.(response.data.codeData);
                //                         }
                //                     }
                //                 }}
                //             >
                //                 {'Send code'.translate(lang)}
                //             </button>
                //         </p>}
                //     </div>;

                //     return <></>;
                // }
            },
            onChangeCallback: checkTaken,
            validator: (value, _, e) => {
                if (Utils.isValidEmail(value)) {
                    if (e?.taken?.emails && Array.isArray(e.taken.emails) && e.taken.emails.includes(value)) return 'Provided email belongs to another user!';
                } else {
                    return 'Please enter a valid email address!'
                }

                return null;
            },
            description: 'We will send a verification code to this email address',
        },
        // emailCode: {
        //     type: 'code',
        //     isRequired: true,
        //     label: 'Email verification code',
        //     config: { autoComplete: 'one-time-code' },
        //     classes: (_, e) => e?.emailCodeData ? { container: 'auth-2fa-verification-code-create-account' } : {},
        //     // styles: (_, e) => e?.emailCodeData ? {} : { container: { display: 'none' } },
        //     condition: (_, e) => e?.emailCodeData ? true : false,
        // },
        phone: {
            type: 'phone',
            isRequired: true,
            label: 'Phone number',
            isDisabled: (_, e) => e?.phoneCodeData ? true : false,
            config: {
                autoComplete: 'tel',
                min: 8,
                max: 20,
                // afterComponent: ({ data: { externalState, submitAttempted, setSubmitError }, value, lang, isValid, hasError, isTouched }) => {
                //     const isDisabled = !value || !isValid || hasError || !isTouched;
                //     if (externalState?.groupData?.useOnlyEmailFor2fa) return <></>;
                //     if (externalState?.phoneCodeData && !externalState?.phoneTempMessage) return <></>
                //     return <div>
                //         {externalState?.phoneTempMessage ? <div className={`auth-2fa-code-after-component-message ${externalState?.phoneTempMessage?.type}`}>
                //             {externalState?.phoneTempMessage?.message}
                //         </div> : null}
                //         {externalState?.phoneCodeData ? null : <p className={`auth-2fa-register-text verification-text ${submitAttempted ? 'verification-text-error' : ''}`}>
                //             {'*'}{'Your phone number must be verified. After typing in and double-checking your number, press the button to send code.'.translate(lang)}
                //             <button
                //                 type='button'
                //                 className='register-button'
                //                 disabled={isDisabled}
                //                 onClick={async () => {
                //                     if (!isDisabled) {
                //                         const data = await onBeforeSubmit({
                //                             _action: '2fa-phone-verification',
                //                             _phoneForVerification: value,
                //                         }, {}, externalState);
                //                         const response = await _fetch('post', '/2fa/create-account', data);
                //                         if (response?.error) {
                //                             externalState?.setPhoneTempMessage?.({ type: 'error', message: response.error.translate(lang) });
                //                         }
                //                         if (response?.data?.codeData) {
                //                             setSubmitError(null);
                //                             externalState?.setPhoneTempMessage?.({ type: 'success', message: 'An SMS with 6-digit code has been sent to your device!'.translate(lang) });
                //                             externalState?.setPhoneCodeData?.(response.data.codeData);
                //                         }
                //                     }
                //                 }}
                //             >
                //                 {'Send code'.translate(lang)}
                //             </button>
                //         </p>}
                //     </div>;

                //     return <></>;
                // }
            },
            description: (_, e) => e?.groupData?.useOnlyEmailFor2fa ? undefined : 'We will send a verification code to this phone number',
        },
        // phoneCode: {
        //     type: 'code',
        //     isRequired: true,
        //     label: 'Phone verification code',
        //     config: { autoComplete: 'one-time-code' },
        //     classes: (_, e) => e?.phoneCodeData ? { container: 'auth-2fa-verification-code-create-account' } : {},
        //     // styles: (_, e) => e?.phoneCodeData ? {} : { container: { display: 'none' } },
        //     condition: (_, e) => e?.groupData?.useOnlyEmailFor2fa ? false : e?.phoneCodeData ? true : false,
        // },
        password: {
            type: 'password',
            isRequired: true,
            label: 'Password',
            initialValue: '',
            config: {
                autoComplete: 'new-password',
                passwordV2: true,
                afterComponent: ({ value, isTouched, data: { externalState } }) => <ul className='auth-2fa-code-after-component-container-list'>
                    <li className={passwordHas8Chars(value) ? 'success' : isTouched ? 'error' : 'gray'}>{'Must be 8 characters long'.translate(externalState?.props?.lang)}</li>
                    <li className={passwordHasUpperCase(value) ? 'success' : isTouched ? 'error' : 'gray'}>{'Must contain uppercase letter'.translate(externalState?.props?.lang)}</li>
                    <li className={passwordHasLowerCase(value) ? 'success' : isTouched ? 'error' : 'gray'}>{'Must contain lowercase letter'.translate(externalState?.props?.lang)}</li>
                    <li className={passwordHasNumber(value) ? 'success' : isTouched ? 'error' : 'gray'}>{'Must contain number'.translate(externalState?.props?.lang)}</li>
                </ul>,
                hideErrorMessage: true,
            },
        },
        emailCode: {
            type: 'code',
            isRequired: true,
            label: 'Email verification code',
            config: { autoComplete: 'one-time-code', hideErrorMessage: (_, e) => e?.hasAttemptedSubmitAfterSendingCode ? false : true },
            classes: (_, e) => e?.emailCodeData ? { container: 'auth-2fa-verification-code-create-account' } : {},
            // styles: (_, e) => e?.emailCodeData ? {} : { container: { display: 'none' } },
            condition: (_, e) => e?.emailCodeData ? true : false,
        },
        phoneCode: {
            type: 'code',
            isRequired: true,
            label: 'Phone verification code',
            config: { autoComplete: 'one-time-code', hideErrorMessage: (_, e) => e?.hasAttemptedSubmitAfterSendingCode ? false : true },
            classes: (_, e) => e?.phoneCodeData ? { container: 'auth-2fa-verification-code-create-account' } : {},
            // styles: (_, e) => e?.phoneCodeData ? {} : { container: { display: 'none' } },
            condition: (_, e) => e?.groupData?.useOnlyEmailFor2fa ? false : e?.phoneCodeData ? true : false,
        },
    }, {
        accountType: type,
        groupData,
        props,
        termsOfUse,
        taken,
        ageLimit,
        lang,

        emailCodeData,
        setEmailCodeData,
        phoneCodeData,
        setPhoneCodeData,

        emailTempMessage,
        setEmailTempMessage,
        phoneTempMessage,
        setPhoneTempMessage,

        hasAttemptedSubmitAfterSendingCode,
    });

    return <Modal
        isOpen={register}
        toggle={() => setRegister(false)}
        backdrop='static'
        centered
        size='md'
    >
        <ModalHeader
            className='login-modal-header' style={{ textAlign: 'center', position: 'relative' }} close={<button type="button" className="close" style={{ position: 'absolute', top: 10, right: 15 }} onClick={() => setRegister(false)}>&times;</button>}
        >
            {props?.groupObj?.logo ? <img style={hideLangSelector || successMessage ? undefined : { marginTop: 50 }} src={API_ENDPOINT + props?.groupObj?.logo} alt='Clinic logo' className="login-clinic-logo" /> : null}
            <p style={!successMessage && (props.onBankId || props.onMitId) ? { marginTop: 15, marginBottom: 20 } : undefined}>{successMessage ? 'Success!'.translate(lang) : 'Create account'.translate(lang)}</p>
            {hideLangSelector || successMessage ? null : <Input
                type='select'
                value={lang}
                onChange={(e) => {
                    const value = e.target.value;
                    props?.setLang?.(value);
                }}
                className='auth-2fa-lang-selector'
            >
                <option key='en' value='en'>{'English'.translate(lang)}</option>
                <option key='se' value='se'>{'Svenska'.translate(lang)}</option>
            </Input>}

            {successMessage || (!props.onBankId && !props.onMitId) ? null : <div className='create-account-other-options-container'>
                <div className='login-btns'>
                    {props.onBankId ? <Button
                        onClick={() => {
                            setRegister(false);
                            props.onBankId?.();
                        }}
                    >
                        <img src={bankidIcon} alt='bank id icon' />
                        {'Create account with BankID'.translate(lang)}
                    </Button> : null}
                    {props.onMitId ? <a
                        onClick={() => {
                            setRegister(false);
                        }}
                        className='button'
                        href={props.onMitId}
                    >
                        <img src={mitIdIcon} alt='mitId icon' />
                        {'Create account with MitID'.translate(lang)}
                    </a> : null}
                </div>
                <div className='auth-divider'>
                    <span>{'or'.translate(lang)}</span>
                </div>
            </div>}

            {successMessage || props?.groupObj?.hideWhoIsTheCaseRegarding ? null : <div className='create-account-account-type-button'>
                <button
                    type='button'
                    onClick={() => {
                        setType(prev => prev === 'adult' ? 'child' : 'adult');
                    }}
                >
                    {buttonMessage}
                </button>
            </div>}
        </ModalHeader>
        <ModalBody className='login-modal-body'>
            {successMessage ? <p className='auth-2fa-reset-password-message'>
                {successMessage.translate(props.lang)}
            </p> : <FormV2
                data={form}
                lang={lang}
            />}
            {successMessage ? null : <p className='auth-2fa-register-text'>
                {'Already have an account?'.translate(lang)}
                {' '}
                <button
                    type='button'
                    className='register-button'
                    onClick={() => {
                        setRegister(false);
                    }}
                >
                    {'Log in'.translate(lang)}
                </button>
            </p>}
            {privacyPolicy && !successMessage ? <p className='auth-2fa-register-text' style={{ fontSize: 12 }}>
                {'Read our'.translate(lang)}
                {' '}
                <strong>
                    <a
                        href={privacyPolicy}
                        target='_blank'
                        style={{ fontSize: 12 }}
                    >
                        {'Privacy Policy'.translate(lang)}
                    </a>
                </strong>
            </p> : null}
        </ModalBody>
        {/* {successMessage ? <ModalFooter style={{ justifyContent: 'center' }}>
            <Button
                color='primary'
                onClick={() => {
                    setRegister(false);
                }}
                style={{ minWidth: 100 }}
            >
                {'Close'.translate(lang)}
            </Button>
        </ModalFooter> : null} */}
    </Modal>
}

const ForgotPassword = ({
    props,
    email,
    setEmail,
    forgotPassword,
    setForgotPassword,
}) => {
    const [tempMessage, setTempMessage] = useState(null);
    const [codeData, setCodeData] = useState(() => _getCodeData('forgot_password', props?.groupObj?._id));
    const [resetPasswordToken, setResetPasswordToken] = useState(null);
    const [numOfManualTries, setNumOfManualTries] = useState(0);
    const [message, setMessage] = useState(null);

    const _fetch = useFetch();

    useEffect(() => {
        let timer = null;
        if (codeData) {
            if (typeof document !== 'undefined') {
                timer = setTimeout(() => {
                    const el = document.getElementById('2fa_forgot_password_code');
                    if (el) el.focus();
                }, 1000);
            }
        }

        return () => {
            if (timer) clearTimeout(timer);
        }
    }, [codeData]);

    useEffect(() => {
        let timer = null;
        if (tempMessage) {
            timer = setTimeout(() => {
                setTempMessage(null);
            }, 5000);
        }

        return () => {
            if (timer) clearTimeout(timer);
        }
    }, [tempMessage]);

    const form = useFormV2({
        autoComplete: true,
        autoSubmit: {
            onBeforeSubmit,
            method: 'post',
            url: '/2fa/forgot-password',
            onAfterSubmit: (data) => {
                if (data?.codeData) {
                    setCodeData(data.codeData);
                    if (data.codeData?.codeId) _setCodeData('forgot_password', data.codeData);
                    form?.set?.('_action', '2fa-forgot-password-code')
                };
                if (data?.token) {
                    setResetPasswordToken(data.token);
                    _removeCodeData('forgot_password');
                    form?.set?.('_action', '2fa-forgot-password-new-password')
                }
                if (data?.message) {
                    setResetPasswordToken(null);
                    setCodeData(null);
                    _removeCodeData('forgot_password');
                    setMessage(data.message);
                }
            },
            skipResetOnSuccess: true,
        },
        buttons: {
            submit: {
                custom: ({ data: { externalState } }) => <button
                    type='submit'
                    className='auth-2fa-button'
                >
                    {externalState?.resetPasswordToken ? 'Reset Password'.translate(externalState?.props?.lang) : externalState?.codeData ? 'Verify'.translate(externalState?.props?.lang) : 'Send'.translate(externalState?.props?.lang)}
                </button>
            }
        },
        classNames: {
            form: 'auth-2fa-form',
            submitError: 'auth-2fa-submit-error',
        },
        useSubmitErrorForErrorMessage: true,
    }, {
        _action: {
            type: 'hidden',
            initialValue: codeData ? '2fa-forgot-password-code' : '2fa-forgot-password'
        },
        email: {
            type: 'email',
            initialValue: email,
            isRequired: true,
            label: 'Email address',
            config: {
                autoComplete: 'off',
                beforeComponent: ({ data: { externalState } }) => <div className='auth-2fa-code-before-component-container'>
                    <p style={{ fontSize: 14 }}>{"Enter email address that you used to create/verify account and we'll send you a 6-digit code to reset the password".translate(externalState?.props?.lang)}</p>
                </div>
            },
            condition: (_, e) => e?.codeData || resetPasswordToken ? false : true,
        },
        code: {
            type: 'code',
            id: '2fa_forgot_password_code',
            isRequired: true,
            condition: (_, e) => e?.codeData && !e?.resetPasswordToken ? true : false,
            config: {
                beforeComponent: ({ data: { externalState } }) => externalState?.codeData?.value ? <div className='auth-2fa-code-before-component-container'>
                    <p style={{ width: '80%', margin: 'auto', marginBottom: 5 }}>{'A 6-digit code has been sent to specified email address, if it exists in our system'.translate(externalState?.props?.lang)}</p>
                    <p>{externalState?.codeData?.device === 'phone' ? <strong>{'+ '}{externalState.codeData.value.slice(0, 2)}{' '}{externalState.codeData.value.slice(2)}</strong> : <strong>{externalState.codeData.value}</strong>}</p>
                </div> : null,
                afterComponent: ({ data: { externalState } }) => externalState?.codeData ? <>
                    <ul className='auth-2fa-code-after-component-container'>
                        <li><p>{'Code is valid for'.translate(externalState?.props?.lang)}{' '}<strong>{'15 minutes'.translate(externalState?.props?.lang)}</strong></p></li>
                        <li><p>{"Didn't receive the code?".translate(externalState?.props?.lang)}{' '}<button
                            type='button'
                            onClick={async () => {
                                if (!externalState?.codeData?.codeId) {
                                    if (externalState?.numOfManualTries >= 3) {
                                        externalState?.setTempMessage?.({
                                            type: 'error',
                                            message: 'You have reached the maximum number of attempts to send the code!'.translate(externalState?.props?.lang),
                                        });
                                    } else {
                                        externalState?.setNumOfManualTries?.(prev => prev + 1);
                                        externalState?.setTempMessage?.({
                                            type: 'success',
                                            message: 'Code has been resent to your device!'.translate(externalState?.props?.lang),
                                        });
                                    }
                                    return;
                                }
                                const data = await onBeforeSubmit({ _action: '2fa-login-code-resend' }, null, externalState);
                                const response = await _fetch('post', '/2fa/resend', data);
                                if (response.data?.message) externalState?.setTempMessage?.({
                                    type: 'success',
                                    message: response.data.message.translate(externalState?.props?.lang),
                                });
                                if (response.error) externalState?.setTempMessage?.({
                                    type: 'error',
                                    message: response.error.translate(externalState?.props?.lang),
                                });
                            }}
                        >
                            {'Resend'.translate(externalState?.props?.lang)}
                        </button></p></li>
                    </ul>
                    {externalState?.tempMessage ? <div className={`auth-2fa-code-after-component-message ${externalState?.tempMessage?.type}`}>
                        {externalState?.tempMessage?.message}
                    </div> : null}
                </> : null,
            }
        },
        password: {
            type: 'password',
            isRequired: true,
            label: 'New password',
            condition: (_, e) => e?.resetPasswordToken ? true : false,
            initialValue: '',
            config: {
                autoComplete: 'new-password',
                passwordV2: true,
                beforeComponent: ({ data: { externalState } }) => <div className='auth-2fa-code-before-component-container'>
                    <p>
                        {'To finish, please enter your new password.'.translate(externalState?.props?.lang)}
                        <br />
                        {"You'll be able to log in with new password.".translate(externalState?.props?.lang)}
                    </p>
                </div>,
                afterComponent: ({ value, isTouched, data: { externalState } }) => <ul className='auth-2fa-code-after-component-container-list'>
                    <li className={passwordHas8Chars(value) ? 'success' : isTouched ? 'error' : 'gray'}>{'Must be 8 characters long'.translate(externalState?.props?.lang)}</li>
                    <li className={passwordHasUpperCase(value) ? 'success' : isTouched ? 'error' : 'gray'}>{'Must contain uppercase letter'.translate(externalState?.props?.lang)}</li>
                    <li className={passwordHasLowerCase(value) ? 'success' : isTouched ? 'error' : 'gray'}>{'Must contain lowercase letter'.translate(externalState?.props?.lang)}</li>
                    <li className={passwordHasNumber(value) ? 'success' : isTouched ? 'error' : 'gray'}>{'Must contain number'.translate(externalState?.props?.lang)}</li>
                </ul>,
                hideErrorMessage: true,
            },
        }
    }, {
        props,
        setForgotPassword,
        codeData,
        tempMessage,
        setTempMessage,
        resetPasswordToken,
        setResetPasswordToken,
        numOfManualTries,
        setNumOfManualTries,
    });

    return <Modal
        isOpen={forgotPassword}
        toggle={() => setForgotPassword(false)}
        backdrop='static'
        centered
        size='md'
    >
        <ModalHeader className='login-modal-header' style={{ textAlign: 'center' }} close={<button type="button" className="close" style={{ position: 'absolute', top: '15px', right: '15px' }} onClick={() => setForgotPassword(false)}>&times;</button>}>
            <img src={forgotPasswordImage} alt='Forgot password' className="login-clinic-logo" />
            <p>{message ? 'Success!'.translate(props.lang) : resetPasswordToken ? 'Choose new password'.translate(props.lang) : 'Forgot your password?'.translate(props.lang)}</p>
        </ModalHeader>
        <ModalBody>
            {message ? <p className='auth-2fa-reset-password-message'>
                {message.translate(props.lang)}
            </p> : <FormV2
                data={form}
                lang={props.lang}
            />}
            <p className='auth-2fa-register-text'>
                {codeData && !resetPasswordToken ? <Fragment key='codeData'>
                    {'Not your account?'.translate(props.lang)}
                    {' '}
                </Fragment> : null}
                <button
                    type='button'
                    className={`register-button ${!message && !(codeData && !resetPasswordToken) ? 'cancel-button' : ''}`}
                    onClick={() => {
                        if (resetPasswordToken) {
                            setResetPasswordToken(null);
                            setCodeData(null);
                            _removeCodeData('forgot_password');
                            form.setSubmitError(null);
                            return;
                        };

                        if (codeData) {
                            setCodeData(null);
                            _removeCodeData('forgot_password');
                            form.setSubmitError(null);
                            return;
                        }

                        setForgotPassword(false);
                    }}
                >
                    {'Cancel'.translate(props.lang)}
                </button>
            </p>
        </ModalBody>
    </Modal>
}

export const VerifyAccountComponent = ({
    userId,
    secretKey,
    alias,
    onLogin,
    onClose,
    ...props
}) => {
    const [successMessage, setSuccessMessage] = useState(null);
    const [emailCodeData, setEmailCodeData] = useState(null);
    const [phoneCodeData, setPhoneCodeData] = useState(null);
    const [emailTempMessage, setEmailTempMessage] = useState(null);
    const [phoneTempMessage, setPhoneTempMessage] = useState(null);
    const [taken, setTaken] = useState({
        ssns: [],
        emails: [],
    });

    const { data: groupData, isLoading: isLoadingGroupData, } = useFetchData(alias ? '/data/clinic-group/info' : null, {
        method: 'post',
        data: {
            alias: alias,
        }
    });
    const { data, isLoading: isLoadingData, error, } = useFetchData('/2fa/check/account', {
        method: 'post',
        data: {
            alias,
            userId,
            secretKey,
        }
    });
    const [hasAttemptedSubmitAfterSendingCode, setHasAttemptedSubmitAfterSendingCode] = useState(false);

    const hideLangSelector = props?.secondaryLanguageDisabled ? true : false;
    const lang = props?.lang ?? 'en';

    const _fetch = useFetch();

    async function checkTaken(value) {
        if (Utils.isValidSSN(value) && value?.ssn) {
            const response = await _fetch('post', '/2fa/check/ssn', {
                ssn: value?.ssn,
            });
            if (response?.data?.ssns) {
                setTaken(prev => ({
                    emails: prev.emails,
                    ssns: [...prev.ssns, ...response.data.ssns],
                }));
                // setTimeout(() => { form?.triggerValidation?.(['child.ssn']); }, 100)
            }
        }
        if (Utils.isValidEmail(value) && value) {
            const response = await _fetch('post', '/2fa/check/email', {
                email: value,
                userId,
            });
            if (response?.data?.emails) {
                setTaken(prev => ({
                    emails: [...prev.emails, ...response.data.emails],
                    ssns: prev.ssns,
                }));
                // setTimeout(() => { form?.triggerValidation?.(); }, 100)
            }
        }
    }

    useEffect(() => {
        let timer = null;
        if (emailTempMessage) timer = setTimeout(() => { setEmailTempMessage(null) }, 5000);
        return () => { clearTimeout(timer) };
    }, [emailTempMessage]);
    useEffect(() => {
        let timer = null;
        if (phoneTempMessage) timer = setTimeout(() => { setPhoneTempMessage(null) }, 5000);
        return () => { clearTimeout(timer) };
    }, [phoneTempMessage]);

    useEffect(() => {
        let tm;
        if (data?.user) {
            const obj = {
                email: data.user?.email || '',
                phone: data.user?.phone || '',
            };
            form.handlePopulate(obj);

            // tm = setTimeout(() => {
            //     const emailInput = document?.getElementById('verify-account-email-input');
            //     if (emailInput) {
            //         emailInput.focus();
            //     }
            // }, 1000);
        }

        return () => { clearTimeout(tm) }
    }, [data])

    const form = useFormV2({
        autoComplete: true,
        autoSubmit: {
            onBeforeSubmit,
            method: 'post',
            url: '/2fa/verify-account',
            onAfterSubmit: (data) => {
                if (data?.message) {
                    setSuccessMessage(data.message);
                    return;
                };
                if (data?.token) {
                    onLogin?.(data);
                }
                if (data?.emailCodeData || data?.phoneCodeData) {
                    if (data?.emailCodeData) setEmailCodeData(data.emailCodeData);
                    if (data?.phoneCodeData) setPhoneCodeData(data.phoneCodeData);

                    form?.set?.('_action', '2fa-create-account');
                }
            },
            skipResetOnSuccess: true,
        },
        buttons: {
            submit: {
                custom: ({ data: { state, externalState } }) => {
                    const r = verificationIsNeeded({
                        groupData: externalState?.groupData,
                        email: state?.email?.value,
                        existingEmail: externalState?._user?.email,
                        emailIsVerified: externalState?._user?.emailIsVerified,
                        phone: state?.phone?.value,
                        existingPhone: externalState?._user?.phone,
                        phoneIsVerified: externalState?._user?.phoneIsVerified,
                    })
                    const login = state?.login?.value;
                    const lang = externalState?.props?.lang ?? 'en';
                    const text = useMemo(() => {
                        if (
                            (r.email && !externalState?.emailCodeData) ||
                            (r.phone && !externalState?.phoneCodeData)
                        ) return 'Send verification code(s)'.translate(lang);

                        if (login) return 'Verify account and Log in'.translate(lang);

                        return 'Verify account'.translate(lang);
                    }, [login, lang, r.email, r.phone, externalState?.emailCodeData, externalState?.phoneCodeData]);

                    return <button
                        type='submit'
                        className='auth-2fa-button'
                        onClick={() => {
                            if (externalState?.emailCodeData || externalState?.phoneCodeData) {
                                setHasAttemptedSubmitAfterSendingCode(true);
                            }
                        }}
                    >
                        {text}
                    </button>
                }
            }
        },
        classNames: {
            form: 'auth-2fa-form',
            submitError: 'auth-2fa-submit-error',
            buttonsContainer: 'auth-2fa-buttons-container',
        },
        useSubmitErrorForErrorMessage: true,
        showValidFields: true,
        // onBeforeSubmitValidator: (data, _, e) => {
        //     let emailVerificationIsNeeded = true;
        //     if (data.email && e?._user?.email && data.email === e._user.email && e._user.emailIsVerified) emailVerificationIsNeeded = false;

        //     let phoneVerificationIsNeeded = true;
        //     if (e?.groupData?.useOnlyEmailFor2fa) phoneVerificationIsNeeded = false;
        //     if (data.phone && e?._user?.phone && data.phone === e._user.phone && e._user.phoneIsVerified) phoneVerificationIsNeeded = false;

        //     if (emailVerificationIsNeeded && !data.emailCode) return 'Please fill in all required fields correctly!';

        //     if (phoneVerificationIsNeeded && !data.phoneCode) return 'Please fill in all required fields correctly!';

        //     return null;
        // },
    }, {
        _action: {
            type: 'hidden',
            initialValue: '2fa-verify-account'
        },
        actionType: {
            type: 'hidden',
            initialValue: 'verification',
            condition: (s, e) => {
                const r = verificationIsNeeded({
                    groupData: e?.groupData,
                    email: s?.email?.value,
                    existingEmail: e?._user?.email,
                    emailIsVerified: e?._user?.emailIsVerified,
                    phone: s?.phone?.value,
                    existingPhone: e?._user?.phone,
                    phoneIsVerified: e?._user?.phoneIsVerified,
                });

                if (
                    (r.email && !e?.emailCodeData) ||
                    (r.phone && !e?.phoneCodeData)
                ) return true;

                return false;
            }
        },
        userId: {
            type: 'hidden',
            isRequired: true,
            initialValue: userId,
        },
        secretKey: {
            type: 'hidden',
            isRequired: true,
            initialValue: secretKey,
        },
        email: {
            type: 'email',
            id: 'verify-account-email-input',
            isRequired: true,
            isDisabled: (_, e) => e?.emailCodeData ? true : false,
            label: 'Email address',
            config: {
                autoComplete: 'email',
                // afterComponent: ({ data: { externalState, submitAttempted, setSubmitError }, value, lang, isValid, hasError }) => {
                //     let emailVerificationIsNeeded = true;
                //     if (value && externalState?._user?.email && value === externalState._user.email && externalState._user.emailIsVerified) emailVerificationIsNeeded = false;

                //     if (!emailVerificationIsNeeded) return null;

                //     const isDisabled = !value || !isValid || hasError;
                //     if (externalState?.emailCodeData && !externalState?.emailTempMessage) return <></>;
                //     return <div>
                //         {externalState?.emailTempMessage ? <div className={`auth-2fa-code-after-component-message ${externalState?.emailTempMessage?.type}`}>
                //             {externalState?.emailTempMessage?.message}
                //         </div> : null}
                //         {externalState?.emailCodeData ? null : <p className={`auth-2fa-register-text verification-text ${submitAttempted ? 'verification-text-error' : ''}`}>
                //             {'*'}{'Your email address must be verified. After typing in and double-checking your email, press the button to send code.'.translate(lang)}
                //             <button
                //                 type='button'
                //                 className='register-button'
                //                 disabled={isDisabled}
                //                 onClick={async () => {
                //                     if (!isDisabled) {
                //                         const data = await onBeforeSubmit({
                //                             _action: '2fa-email-verification',
                //                             _emailForVerification: value,
                //                             userId,
                //                         }, {}, externalState);
                //                         const response = await _fetch('post', '/2fa/create-account', data);
                //                         if (response?.error) {
                //                             externalState?.setEmailTempMessage?.({ type: 'error', message: response.error.translate(lang) });
                //                         }
                //                         if (response?.data?.codeData) {
                //                             setSubmitError(null);
                //                             externalState?.setEmailTempMessage?.({ type: 'success', message: 'A 6-digit code has been sent to specified email address!'.translate(lang) });
                //                             externalState?.setEmailCodeData?.(response.data.codeData);
                //                         }
                //                     }
                //                 }}
                //             >
                //                 {'Send code'.translate(lang)}
                //             </button>
                //         </p>}
                //     </div>;
                // }
            },
            onChangeCallback: checkTaken,
            validator: (value, _, e) => {
                if (Utils.isValidEmail(value)) {
                    if (e?.taken?.emails && Array.isArray(e.taken.emails) && e.taken.emails.includes(value)) return 'Provided email belongs to another user!';
                } else {
                    return 'Please enter a valid email address!'
                }

                return null;
            },
            description: (s, e) => {
                const r = verificationIsNeeded({
                    groupData: e?.groupData,
                    email: s?.email?.value,
                    existingEmail: e?._user?.email,
                    emailIsVerified: e?._user?.emailIsVerified,
                    phone: s?.phone?.value,
                    existingPhone: e?._user?.phone,
                    phoneIsVerified: e?._user?.phoneIsVerified,
                });

                if (r?.email && !e?.emailCodeData) return 'We will send a verification code to this email address'
            }
        },
        // emailCode: {
        //     type: 'code',
        //     isRequired: true,
        //     label: 'Email verification code',
        //     config: { autoComplete: 'one-time-code' },
        //     classes: (_, e) => e?.emailCodeData ? { container: 'auth-2fa-verification-code-create-account' } : {},
        //     // styles: (_, e) => e?.emailCodeData ? {} : { container: { display: 'none' } },
        //     condition: (_, e) => e?.emailCodeData ? true : false,
        // },
        phone: {
            type: 'phone',
            isRequired: true,
            label: 'Phone number',
            isDisabled: (_, e) => e?.phoneCodeData ? true : false,
            config: {
                autoComplete: 'tel',
                min: 8,
                max: 20,
                // afterComponent: ({ data: { externalState, submitAttempted, setSubmitError }, value, lang, isValid, hasError }) => {
                //     let phoneVerificationIsNeeded = true;
                //     if (externalState?.groupData?.useOnlyEmailFor2fa) phoneVerificationIsNeeded = false;
                //     if (value && externalState?._user?.phone && value === externalState._user.phone && externalState._user.phoneIsVerified) phoneVerificationIsNeeded = false;

                //     if (!phoneVerificationIsNeeded) return null;

                //     const isDisabled = !value || !isValid || hasError;
                //     if (externalState?.phoneCodeData && !externalState?.phoneTempMessage) return <></>;
                //     return <div>
                //         {externalState?.phoneTempMessage ? <div className={`auth-2fa-code-after-component-message ${externalState?.phoneTempMessage?.type}`}>
                //             {externalState?.phoneTempMessage?.message}
                //         </div> : null}
                //         {externalState?.phoneCodeData ? null : <p className={`auth-2fa-register-text verification-text ${submitAttempted ? 'verification-text-error' : ''}`}>
                //             {'*'}{'Your phone number must be verified. After typing in and double-checking your number, press the button to send code.'.translate(lang)}
                //             <button
                //                 type='button'
                //                 className='register-button'
                //                 disabled={isDisabled}
                //                 onClick={async () => {
                //                     if (!isDisabled) {
                //                         const data = await onBeforeSubmit({
                //                             _action: '2fa-phone-verification',
                //                             _phoneForVerification: value,
                //                             userId,
                //                         }, {}, externalState);
                //                         const response = await _fetch('post', '/2fa/create-account', data);
                //                         if (response?.error) {
                //                             externalState?.setPhoneTempMessage?.({ type: 'error', message: response.error.translate(lang) });
                //                         }
                //                         if (response?.data?.codeData) {
                //                             setSubmitError(null);
                //                             externalState?.setPhoneTempMessage?.({ type: 'success', message: 'An SMS with 6-digit code has been sent to your device!'.translate(lang) });
                //                             externalState?.setPhoneCodeData?.(response.data.codeData);
                //                         }
                //                     }
                //                 }}
                //             >
                //                 {'Send code'.translate(lang)}
                //             </button>
                //         </p>}
                //     </div>;
                // }
            },
            description: (s, e) => {
                const r = verificationIsNeeded({
                    groupData: e?.groupData,
                    email: s?.email?.value,
                    existingEmail: e?._user?.email,
                    emailIsVerified: e?._user?.emailIsVerified,
                    phone: s?.phone?.value,
                    existingPhone: e?._user?.phone,
                    phoneIsVerified: e?._user?.phoneIsVerified,
                });

                if (r?.phone && !e?.phoneCodeData) return 'We will send a verification code to this phone number'
            }
        },
        // phoneCode: {
        //     type: 'code',
        //     isRequired: true,
        //     label: 'Phone verification code',
        //     config: { autoComplete: 'one-time-code' },
        //     classes: (_, e) => e?.phoneCodeData ? { container: 'auth-2fa-verification-code-create-account' } : {},
        //     // styles: (_, e) => e?.phoneCodeData ? {} : { container: { display: 'none' } },
        //     condition: (_, e) => e?.groupData?.useOnlyEmailFor2fa ? false : e?.phoneCodeData ? true : false,
        // },
        password: {
            type: 'password',
            isRequired: true,
            label: 'Password',
            initialValue: '',
            config: {
                autoComplete: 'new-password',
                passwordV2: true,
                afterComponent: ({ value, isTouched, data: { externalState } }) => <ul className='auth-2fa-code-after-component-container-list'>
                    <li className={passwordHas8Chars(value) ? 'success' : isTouched ? 'error' : 'gray'}>{'Must be 8 characters long'.translate(externalState?.props?.lang)}</li>
                    <li className={passwordHasUpperCase(value) ? 'success' : isTouched ? 'error' : 'gray'}>{'Must contain uppercase letter'.translate(externalState?.props?.lang)}</li>
                    <li className={passwordHasLowerCase(value) ? 'success' : isTouched ? 'error' : 'gray'}>{'Must contain lowercase letter'.translate(externalState?.props?.lang)}</li>
                    <li className={passwordHasNumber(value) ? 'success' : isTouched ? 'error' : 'gray'}>{'Must contain number'.translate(externalState?.props?.lang)}</li>
                </ul>,
                hideErrorMessage: true,
            },
            condition: (_, e) => e?._user?.hasPk ? false : true,
        },
        oldPassword: {
            type: 'password',
            isRequired: (s) => s?.oldPassword?.value || s?.newPassword?.value ? true : false,
            label: 'Old Password',
            initialValue: '',
            config: {
                passwordV2: true,
            },
            condition: (_, e) => e?._user?.hasPk ? true : false,
            description: `Your account already has password set. If you wish to change your current password, please enter your old password and your new password. Otherwise, ignore this field.`
        },
        newPassword: {
            type: 'password',
            isRequired: (s) => s?.oldPassword?.value || s?.newPassword?.value ? true : false,
            label: 'New Password',
            initialValue: '',
            config: {
                autoComplete: 'new-password',
                passwordV2: true,
                afterComponent: ({ value, isTouched, data: { externalState, state } }) => {
                    let isRequired = externalState?._user?.hasPk && (value || state?.oldPassword?.value) ? true : false;
                    return <ul className='auth-2fa-code-after-component-container-list'>
                        <li className={passwordHas8Chars(value) ? 'success' : isTouched && isRequired ? 'error' : 'gray'}>{'Must be 8 characters long'.translate(externalState?.props?.lang)}</li>
                        <li className={passwordHasUpperCase(value) ? 'success' : isTouched && isRequired ? 'error' : 'gray'}>{'Must contain uppercase letter'.translate(externalState?.props?.lang)}</li>
                        <li className={passwordHasLowerCase(value) ? 'success' : isTouched && isRequired ? 'error' : 'gray'}>{'Must contain lowercase letter'.translate(externalState?.props?.lang)}</li>
                        <li className={passwordHasNumber(value) ? 'success' : isTouched && isRequired ? 'error' : 'gray'}>{'Must contain number'.translate(externalState?.props?.lang)}</li>
                    </ul>
                },
                hideErrorMessage: true,
            },
            condition: (s, e) => e?._user?.hasPk ? true : false,
        },
        emailCode: {
            type: 'code',
            isRequired: true,
            label: 'Email verification code',
            config: { autoComplete: 'one-time-code', hideErrorMessage: (_, e) => e?.hasAttemptedSubmitAfterSendingCode ? false : true },
            classes: (_, e) => e?.emailCodeData ? { container: 'auth-2fa-verification-code-create-account' } : {},
            condition: (_, e) => e?.emailCodeData ? true : false,
        },
        phoneCode: {
            type: 'code',
            isRequired: true,
            label: 'Phone verification code',
            config: { autoComplete: 'one-time-code', hideErrorMessage: (_, e) => e?.hasAttemptedSubmitAfterSendingCode ? false : true },
            classes: (_, e) => e?.phoneCodeData ? { container: 'auth-2fa-verification-code-create-account' } : {},
            condition: (_, e) => e?.groupData?.useOnlyEmailFor2fa ? false : e?.phoneCodeData ? true : false,
        },
        login: {
            type: 'checkbox',
            initialValue: false,
            label: 'Log in now',
            condition: (_, e) => !e?.emailCodeData && !e?.phoneCodeData ? false : e?.props?.uData?._id ? false : true,
        }
    }, {
        groupData,
        props,
        taken,

        _user: data?.user,

        emailCodeData,
        setEmailCodeData,
        phoneCodeData,
        setPhoneCodeData,

        emailTempMessage,
        setEmailTempMessage,
        phoneTempMessage,
        setPhoneTempMessage,

        hasAttemptedSubmitAfterSendingCode,
        setHasAttemptedSubmitAfterSendingCode
    })

    const isLoading = isLoadingGroupData || isLoadingData;
    return <Modal
        isOpen={props?.isOpen}
        toggle={onClose}
        backdrop='static'
        centered
        size='md'
    >
        <ModalHeader
            className='login-modal-header' style={{ textAlign: 'center', position: 'relative' }} close={<button type="button" className="close" style={{ position: 'absolute', top: 10, right: 15 }} onClick={onClose}>&times;</button>}
        >
            {isLoading ? null : <>
                {groupData?.logo ? <img style={hideLangSelector || successMessage ? undefined : { marginTop: 50 }} src={API_ENDPOINT + groupData?.logo} alt='Clinic logo' className="login-clinic-logo" /> : null}
                {successMessage ? <p>{'Success!'.translate(lang)}</p> : error ? null : <p>{'Verify account'.translate(lang)}</p>}
                {hideLangSelector || successMessage ? null : <Input
                    type='select'
                    value={lang}
                    onChange={(e) => {
                        const value = e.target.value;
                        props?.setLang?.(value);
                    }}
                    className='auth-2fa-lang-selector'
                >
                    <option key='en' value='en'>{'English'.translate(lang)}</option>
                    <option key='se' value='se'>{'Svenska'.translate(lang)}</option>
                </Input>}
            </>}
        </ModalHeader>
        {isLoading ? <ModalBody
            key='loading'
            className='form-modal-loading'
        >
            <div className="loader-wrap" >
                <Player
                    autoplay={true}
                    loop={true}
                    src="/lf30_editor_l5cxzdyf.json"
                    style={{ height: typeof window != 'undefined' && window.innerWidth < 768 ? "128px" : "256px", width: typeof window != 'undefined' && window.innerWidth < 768 ? "128px" : "256px" }}
                ></Player>
            </div>
        </ModalBody> : <ModalBody
            key='form'
        >
            {successMessage || error ? <p className='auth-2fa-reset-password-message'>
                {successMessage ? successMessage.translate(props.lang) : (error?.translate(lang) ?? error)}
            </p> : <FormV2
                data={form}
                lang={lang}
            />}
        </ModalBody>}
    </Modal>
}